import { useMemo, forwardRef } from 'react';
import { Document } from '../types/document';
import StatusBadge from './StatusBadge';
import { ExtractionActions } from './documents/ExtractionActions';
import { LockBadge } from './documents/LockBadge';
import { FilterableTable, type FilterableColumnDef, type FilterableTableHandle } from './ui/FilterableTable';
import { FlagBadgeList } from './FlagBadge';
import { ConfidenceScoreBadge } from './ConfidenceScoreBadge';
import {
  CONFIDENCE_LEVELS,
  getConfidenceLevel,
  type ConfidenceLevel,
} from '../lib/confidenceLevel';

interface Props {
  documents: Document[];
  onSelect: (doc: Document) => void;
  onActionComplete?: () => void;
  selectable?: boolean;
  selectedKeys?: Set<string | number>;
  onSelectionChange?: (keys: Set<string | number>) => void;
  /** Conserve la sélection entre pages/filtres (désactive l'auto-purge). */
  keepSelectionOnFilter?: boolean;
  onFilteredCountChange?: (filtered: number, total: number) => void;
  /** Tenant extraction policy — used to gate the "Lancer extraction" button. */
  extractionPolicy?: string;
  /** Current authenticated user id — used to hide LockBadge when self-locked. */
  currentUserId?: string;
  /** Server-side sort callback (PROD1.4.7 #496). When provided, tri delegated to backend. */
  onSortChange?: (key: string | null, direction: 'asc' | 'desc' | null) => void;
  /** Controlled sort state (paired with onSortChange). */
  controlledSort?: { key: string; direction: 'asc' | 'desc' } | null;
  /** Server-side filter callback (PROD1.4.7 server filters). */
  onFiltersChange?: (key: string, selected: string[]) => void;
  controlledFilters?: Record<string, string[]>;
  /** Server-side search callback. */
  onSearchChange?: (key: string, text: string) => void;
  controlledSearches?: Record<string, string>;
  /**
   * Distinct values aggregated server-side (across the full dataset, not just
   * the page). Used to populate filter dropdowns instead of auto-extracting
   * from the paginated `documents` (which would yield only the values
   * present on the current page).
   */
  filterFacets?: {
    domains?: string[];
    tags?: string[];
    flags?: string[];
    schema_versions?: string[];
    prompt_versions?: string[];
    llm_versions?: string[];
    ocr_versions?: string[];
    people?: string[];
  };
}

export const DocumentList = forwardRef<FilterableTableHandle, Props>(function DocumentList({ documents, onSelect, onActionComplete, selectable, selectedKeys, onSelectionChange, keepSelectionOnFilter, onFilteredCountChange, extractionPolicy, currentUserId, onSortChange, controlledSort, onFiltersChange, controlledFilters, onSearchChange, controlledSearches, filterFacets }, ref) {
  const columns = useMemo<FilterableColumnDef<Document>[]>(() => [
    {
      key: 'filename',
      header: 'Nom du fichier',
      sortable: true,
      searchable: true,
      sortValueFn: (doc) => doc.original_filename.toLowerCase(),
      render: (doc) => (
        <div className="flex flex-col gap-0.5">
          <span
            className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
            onClick={() => onSelect(doc)}
          >
            {doc.original_filename}
          </span>
          {currentUserId && (
            <LockBadge
              lockedById={doc.locked_by_id}
              lockedByEmail={doc.locked_by_email}
              lockExpiresAt={doc.lock_expires_at}
              currentUserId={currentUserId}
            />
          )}
        </div>
      ),
    },
    {
      key: 'domain',
      header: 'Domaine',
      filterable: true,
      searchable: true,
      filterOptions: filterFacets?.domains?.map((d) => ({ value: d, label: d })),
      render: (doc) => <span className="text-sm text-gray-500">{doc.domain || '-'}</span>,
    },
    {
      key: 'tags',
      header: 'Tags',
      filterable: true,
      filterValueFn: (doc) => doc.tags || [],
      filterOptions: filterFacets?.tags?.map((t) => ({ value: t, label: t })),
      render: (doc) => (
        <div className="flex flex-wrap gap-1">
          {(doc.tags || []).map((tag) => (
            <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
              {tag}
            </span>
          ))}
        </div>
      ),
    },
    {
      // PROD1.7.8 N2 — flags LLM (cas complexes détectés sur l'extraction courante).
      // Filter options dérivées de /filter-facets (dynamique : nouveau flag ajouté à
      // ALLOWED_FLAGS apparaît dans le dropdown dès qu'il est détecté sur un doc).
      key: 'flags',
      header: 'Flags LLM',
      filterable: true,
      filterValueFn: (doc) => doc.flags || [],
      filterOptions: filterFacets?.flags?.map((f) => ({ value: f, label: f })),
      render: (doc) => <FlagBadgeList flags={doc.flags || []} compact />,
    },
    {
      // PROD1.7.8 N3 — score de confiance global de l'extraction courante.
      // Filtrage client par bucket (excellent / good / attention / weak / unknown).
      // Tri sur le score brut (NULL en fin pour ne pas masquer les faibles scores).
      key: 'confidence_score',
      header: 'Confiance',
      filterable: true,
      sortable: true,
      filterValueFn: (doc): ConfidenceLevel => getConfidenceLevel(doc.confidence_score),
      filterOptions: (['weak', 'attention', 'good', 'excellent', 'unknown'] as ConfidenceLevel[]).map((l) => ({
        value: l,
        label: CONFIDENCE_LEVELS[l].rangeLabel,
      })),
      sortValueFn: (doc) =>
        // tri ASC : faible → excellent ; les unknown poussés en fin via "9.99".
        doc.confidence_score === null || doc.confidence_score === undefined
          ? '9.99'
          : String(doc.confidence_score).padStart(5, '0'),
      render: (doc) => <ConfidenceScoreBadge score={doc.confidence_score} />,
    },
    {
      key: 'status',
      header: 'Statut',
      filterable: true,
      filterOptions: [
        { value: 'uploaded', label: 'Uploadé' },
        { value: 'processing', label: 'En cours' },
        { value: 'extracted', label: 'Extrait' },
        { value: 'pending_validation', label: 'En validation' },
        { value: 'validated', label: 'Validé' },
        { value: 'error', label: 'Erreur' },
      ],
      render: (doc) => <StatusBadge status={doc.status} />,
    },
    {
      // PROD1.7.8 N2 (S2) — fusion S/P/L/O dans une seule cellule 2x2 pour
      // libérer 3 colonnes (la nouvelle colonne "Flags LLM" cassait le
      // formatage). Les 4 filtres + tri restent INDÉPENDANTS via subFilters
      // (URL params inchangés : schema_version, prompt_version, llm_version,
      // ocr_version → handleFiltersChange existant reste compatible).
      key: 'configs',
      header: 'Configs',
      width: '150px',
      subFilters: [
        {
          key: 'schema_version',
          label: 'Schéma',
          filterOptions: (filterFacets?.schema_versions || []).map((v) => ({ value: v, label: v })),
          filterValueFn: (doc) => doc.schema_version || '',
        },
        {
          key: 'prompt_version',
          label: 'Prompt',
          filterOptions: (filterFacets?.prompt_versions || []).map((v) => ({ value: v, label: v })),
          filterValueFn: (doc) => doc.prompt_version || '',
        },
        {
          key: 'llm_version',
          label: 'LLM',
          filterOptions: (filterFacets?.llm_versions || []).map((v) => ({ value: v, label: v })),
          filterValueFn: (doc) => doc.llm_version || '',
        },
        {
          key: 'ocr_version',
          label: 'OCR',
          filterOptions: (filterFacets?.ocr_versions || []).map((v) => ({ value: v, label: v })),
          filterValueFn: (doc) => doc.ocr_version || '',
        },
      ],
      sortOptions: [
        { key: 'schema_version', label: 'Schéma', sortValueFn: (doc) => doc.schema_version || '' },
        { key: 'prompt_version', label: 'Prompt', sortValueFn: (doc) => doc.prompt_version || '' },
        { key: 'llm_version', label: 'LLM', sortValueFn: (doc) => doc.llm_version || '' },
        { key: 'ocr_version', label: 'OCR', sortValueFn: (doc) => doc.ocr_version || '' },
      ],
      render: (doc) => {
        const Cell = ({ label, value }: { label: string; value?: string }) => (
          <div className="flex items-baseline gap-1 text-xs whitespace-nowrap text-gray-500">
            <span className="text-gray-400 flex-shrink-0">{label}</span>
            <span className="text-gray-700">{value || '—'}</span>
          </div>
        );
        return (
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
            <Cell label="S:" value={doc.schema_version} />
            <Cell label="P:" value={doc.prompt_version} />
            <Cell label="L:" value={doc.llm_version} />
            <Cell label="O:" value={doc.ocr_version} />
          </div>
        );
      },
    },
    {
      key: 'people',
      header: 'Personnes',
      width: '170px',
      // Filtre serveur multi-value (?people=Alice&people=Bob → OR) ET
      // search texte libre (?people=substring → ilike). Le dropdown
      // expose les valeurs distinctes du tenant (full_name | username)
      // fournies par `/filter-facets`.
      filterable: true,
      searchable: true,
      filterOptions: filterFacets?.people?.map((p) => ({ value: p, label: p })),
      filterValueFn: (doc) => [
        doc.uploaded_by_name,
        doc.extracted_by_name,
        doc.validated_by_name,
      ].filter((n): n is string => !!n),
      render: (doc) => {
        const Row = ({ label, value }: { label: string; value?: string | null }) => (
          <div className="flex items-baseline gap-1.5 text-xs whitespace-nowrap text-gray-500">
            <span className="w-4 text-gray-400 flex-shrink-0">{label}</span>
            <span className="truncate">{value || '—'}</span>
          </div>
        );
        return (
          <div className="space-y-0.5">
            <Row label="U:" value={doc.uploaded_by_name} />
            <Row label="E:" value={doc.extracted_by_name} />
            <Row label="V:" value={doc.validated_by_name} />
          </div>
        );
      },
    },
    {
      key: 'dates',
      header: 'Dates',
      width: '170px',
      sortOptions: [
        {
          key: 'last_action',
          label: 'Dernière action',
          sortValueFn: (doc) => new Date(doc.last_action_at || doc.created_at).getTime(),
        },
        {
          key: 'upload',
          label: 'Date upload',
          sortValueFn: (doc) => new Date(doc.created_at).getTime(),
        },
        {
          key: 'extracted',
          label: 'Date extraction',
          sortValueFn: (doc) => doc.extracted_at ? new Date(doc.extracted_at).getTime() : 0,
        },
        {
          key: 'validated',
          label: 'Date validation',
          sortValueFn: (doc) => doc.validated_at ? new Date(doc.validated_at).getTime() : 0,
        },
      ],
      render: (doc) => {
        const fmt = (iso?: string | null) => {
          if (!iso) return null;
          // fr-CA injecte des espaces autour du 'h' (ex. "14 h 30") même avec
          // hourCycle h23 — on construit donc le format manuellement à partir des
          // parts pour obtenir "2026-05-10 14:30".
          const parts = new Intl.DateTimeFormat('fr-CA', {
            timeZone: 'America/Montreal',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
          }).formatToParts(new Date(iso));
          const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
          return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;
        };
        const upload = fmt(doc.created_at);
        const extracted = fmt(doc.extracted_at);
        const validated = fmt(doc.validated_at);
        // Plus récente parmi les 3 → mise en évidence
        const lastIso =
          doc.validated_at || doc.extracted_at || doc.created_at;
        const Row = ({ label, iso, value }: { label: string; iso?: string | null; value: string | null }) => {
          const isLatest = !!value && iso === lastIso;
          return (
            <div className={`flex items-baseline gap-1.5 text-xs whitespace-nowrap ${isLatest ? 'font-medium text-gray-800' : 'text-gray-500'}`}>
              <span className="w-4 text-gray-400 flex-shrink-0">{label}</span>
              <span>{value || '—'}</span>
            </div>
          );
        };
        // Ordre fixe U / E / V même quand certaines dates sont absentes,
        // pour que les colonnes restent alignées d'une ligne à l'autre.
        return (
          <div className="space-y-0.5">
            <Row label="U:" iso={doc.created_at} value={upload} />
            <Row label="E:" iso={doc.extracted_at} value={extracted} />
            <Row label="V:" iso={doc.validated_at} value={validated} />
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (doc) => (
        <ExtractionActions
          document={doc}
          onActionComplete={onActionComplete}
          extractionPolicy={extractionPolicy}
        />
      ),
    },
  ], [onSelect, onActionComplete, extractionPolicy, currentUserId, filterFacets]);

  // In server mode the parent owns the dataset — even with 0 docs we render
  // the table header so the user can clear filters. In legacy client-side
  // mode we keep the original "no documents" empty placeholder.
  const isServerMode = !!onFiltersChange || !!onSearchChange;
  if (!isServerMode && documents.length === 0) {
    return <p className="text-gray-500 text-center py-8">Aucun document</p>;
  }

  return (
    <FilterableTable
      ref={ref}
      data={documents}
      columns={columns}
      rowKey={(doc) => doc.id}
      defaultSort={{ key: 'dates::last_action', direction: 'desc' }}
      noDataMessage="Aucun document disponible"
      emptyMessage="Aucun document ne correspond aux filtres"
      selectable={selectable}
      selectedKeys={selectedKeys}
      onSelectionChange={onSelectionChange}
      keepSelectionOnFilter={keepSelectionOnFilter}
      onFilteredCountChange={onFilteredCountChange}
      onSortChange={onSortChange}
      controlledSort={controlledSort}
      onFiltersChange={onFiltersChange}
      controlledFilters={controlledFilters}
      onSearchChange={onSearchChange}
      controlledSearches={controlledSearches}
    />
  );
});
